<script setup lang="ts">
  import { computed, getCurrentInstance, ref } from 'vue';
  import PosUiButton from '@/components/pos-v2/ui/PosUiButton.vue';
  import PosUiField from '@/components/pos-v2/ui/PosUiField.vue';
  import PosUiInput from '@/components/pos-v2/ui/PosUiInput.vue';
  import {
    IQuickPriceLine,
    quickPriceChange,
    quickPriceLines,
    TPriceFieldError,
    validateQuickPrice,
  } from '@/domain/catalog/price-form';
  import { currencySymbol } from '@/domain/store-setup/money';
  import { IBulkPriceChange } from '@/interfaces/ICatalogV2';
  import { IItem } from '@/interfaces/IItem';

  /**
   * «Cambiar precio» (plan §5.9): one field per size in order (paused sizes
   * say so) or a single «Precio», plus «Con descuento» on the lines that
   * have one. Enter saves (the form's own submit); only the lines the owner
   * changed are sent. The panel host focuses the first field.
   */
  const props = defineProps<{
    item: IItem;
    currency: string;
    country: string;
    /** Rendered in the phone sheet: the orange primary saves. */
    sheet: boolean;
    saving: boolean;
    online: boolean;
  }>();

  const emit = defineEmits<{
    /** null: nothing changed. */
    (e: 'save', change: IBulkPriceChange | null): void;
    (e: 'cancel'): void;
  }>();

  const proxy = getCurrentInstance()!.proxy;
  const t = (key: string, params?: Record<string, unknown>) =>
    String(proxy.$t(key, params));

  const lines = ref<IQuickPriceLine[]>(
    quickPriceLines(props.item, props.country)
  );
  /** Errors show once a field was edited, or after a save attempt. */
  const touched = ref<Record<string, true>>({});
  const submitted = ref(false);

  const symbol = computed(() => currencySymbol(props.currency));
  const errors = computed(() => validateQuickPrice(lines.value));
  const valid = computed(() => Object.keys(errors.value).length === 0);

  const ERROR_KEYS: Record<TPriceFieldError, string> = {
    required: 'menuPage.price.required',
    invalid: 'menuPage.price.invalid',
    discountTooHigh: 'menuPage.price.discountTooHigh',
  };

  function fieldId(line: IQuickPriceLine, field: 'price' | 'discount') {
    return `quick-price-${props.item.id}-${line.key}-${field}`;
  }

  function errorOf(
    line: IQuickPriceLine,
    field: 'price' | 'discount'
  ): string | undefined {
    const error = errors.value[line.key]?.[field];
    if (!error) return undefined;
    if (!submitted.value && !touched.value[fieldId(line, field)])
      return undefined;
    return t(ERROR_KEYS[error]);
  }

  function lineLabel(line: IQuickPriceLine): string {
    if (line.label === null) return t('menuPage.price.price');
    return line.paused
      ? t('menuPage.price.paused', { name: line.label })
      : line.label;
  }

  function setPrice(index: number, text: string) {
    const line = lines.value[index];
    touched.value = { ...touched.value, [fieldId(line, 'price')]: true };
    lines.value.splice(index, 1, { ...line, priceText: text });
  }

  function setDiscount(index: number, text: string) {
    const line = lines.value[index];
    touched.value = { ...touched.value, [fieldId(line, 'discount')]: true };
    lines.value.splice(index, 1, { ...line, discountText: text });
  }

  function save() {
    submitted.value = true;
    if (!valid.value || props.saving || !props.online) return;
    emit('save', quickPriceChange(props.item, lines.value));
  }
</script>

<template>
  <form class="v2-catalog-quick-price" novalidate @submit.prevent="save">
    <h2 class="price-title">{{ $t('menuPage.price.title') }}</h2>
    <p class="price-name" :title="item.name">{{ item.name }}</p>

    <div
      v-for="(line, index) in lines"
      :key="line.key"
      class="price-line"
      :class="{ 'has-discount': line.discountText !== null }"
    >
      <PosUiField
        class="line-field"
        :label="lineLabel(line)"
        :input-id="fieldId(line, 'price')"
        :error="errorOf(line, 'price')"
      >
        <PosUiInput
          :id="fieldId(line, 'price')"
          :value="line.priceText"
          :prefix="symbol"
          inputmode="decimal"
          autocomplete="off"
          @input="setPrice(index, $event)"
        />
      </PosUiField>
      <PosUiField
        v-if="line.discountText !== null"
        class="line-field"
        :label="$t('menuPage.price.discount')"
        :input-id="fieldId(line, 'discount')"
        :error="errorOf(line, 'discount')"
      >
        <PosUiInput
          :id="fieldId(line, 'discount')"
          :value="line.discountText"
          :prefix="symbol"
          inputmode="decimal"
          autocomplete="off"
          @input="setDiscount(index, $event)"
        />
      </PosUiField>
    </div>

    <div class="price-actions">
      <PosUiButton color="neutral" variant="ghost" @click="emit('cancel')">{{
        $t('cancel')
      }}</PosUiButton>
      <PosUiButton
        class="price-save"
        type="submit"
        :color="sheet ? 'primary' : 'neutral'"
        :disabled="!valid || !online || saving"
        :title="online ? undefined : $t('menuPage.errors.offline')"
        >{{ $t('save') }}</PosUiButton
      >
    </div>
  </form>
</template>

<style scoped lang="scss">
  .v2-catalog-quick-price {
    .price-title {
      @apply tw-m-0 tw-text-base tw-font-semibold tw-leading-snug tw-text-neutral-900;
    }

    .price-name {
      @apply tw-mb-0 tw-mt-0.5 tw-truncate tw-text-sm tw-text-neutral-500;
    }

    .price-line {
      @apply tw-mt-3 tw-flex tw-items-start;

      .line-field {
        @apply tw-min-w-0 tw-flex-1;
      }

      .line-field + .line-field {
        @apply tw-ml-2;
      }
    }

    .price-actions {
      @apply tw-mt-4 tw-flex tw-justify-end;

      .price-save {
        @apply tw-ml-2;
      }
    }
  }
</style>
